import { Contact } from '@eigtech/contacts-types'
import { getContactEmailAddress } from '@eigtech/contacts-util'
import { getContactName, useGetSubcontractorBook } from '@eigtech/ui-shared-contacts'
import { Stack, Text } from '@eigtech/ui-shared-dave'
import {
  FieldValues,
  FormFieldControl,
  FormFieldControlProps,
  WithFieldNameProp,
  useController,
} from '@eigtech/ui-shared-forms'
import {
  GroupBase,
  Props,
  Select,
  SelectComponentsConfig,
  SelectInstance,
  chakraComponents,
} from 'chakra-react-select'
import { ReactElement, ReactNode, RefAttributes, forwardRef, useMemo } from 'react'

export const SubcontractorSelect = forwardRef(function SubcontractorSelect(
  { omitContacts, ...props },
  ref
) {
  const { data, isPending } = useGetSubcontractorBook()

  const contacts = useMemo(
    () =>
      (data?.contacts ?? []).filter((contact) => !(omitContacts ?? []).includes(contact.contactId)),
    [data?.contacts, omitContacts]
  )

  return (
    <Select
      ref={ref}
      {...props}
      components={selectComponents}
      getOptionLabel={(contact) => getContactName(contact)}
      getOptionValue={(contact) => contact.contactId}
      isLoading={isPending}
      // Re-enabling may cause this field in drawers to break. Needs more testing.
      // menuPortalTarget={document.body}
      options={contacts}
    />
  )
}) as (
  props: Props<Contact, false, GroupBase<Contact>> & {
    omitContacts?: Contact['contactId'][]
  } & RefAttributes<SelectInstance<Contact, false, GroupBase<Contact>>>
) => ReactElement

const selectComponents: SelectComponentsConfig<Contact, false, GroupBase<Contact>> = {
  Option: ({ children, ...props }) => (
    <chakraComponents.Option {...props}>
      <Stack spacing="0">
        <Text as="span">{children}</Text>
        <Text as="span" color={props.isSelected ? 'whiteAlpha.700' : 'gray.600'} fontSize="xs">
          {getContactEmailAddress(props.data) ?? 'No email address'}
        </Text>
      </Stack>
    </chakraComponents.Option>
  ),
}

export type SubcontractorSelectFieldProps<TFieldValues extends FieldValues = FieldValues> =
  WithFieldNameProp<
    Omit<Props<Contact, false, GroupBase<Contact>>, keyof FormFieldControlProps>,
    TFieldValues
  > & {
    label: ReactNode
    fieldProps?: Omit<FormFieldControlProps, 'label' | 'name'>
    omitContacts?: Contact['contactId'][]
  }

export function SubcontractorSelectField<TFieldValues extends FieldValues = FieldValues>({
  name,
  label,
  fieldProps,
  ...selectProps
}: SubcontractorSelectFieldProps<TFieldValues>) {
  const {
    field,
    formState: { isSubmitting },
  } = useController({
    name,
    control: fieldProps?.control,
  })

  return (
    <FormFieldControl {...fieldProps} label={label} name={name}>
      <SubcontractorSelect {...field} isDisabled={isSubmitting} {...selectProps} />
    </FormFieldControl>
  )
}
